import { useEffect, useRef, useState } from "react";
import styled, { css, up } from "@xstyled/styled-components";
import { Link, Outlet, useNavigate } from "react-router-dom";

import { FilterableJobList } from "./FilterableJobList";
import { PublishedJobList } from "./PublishedJobList";
import { JobListMode } from "./statusTypes";
import { MissingSalaryWarning } from "./MissingSalaryWarning";
import { DisapprovedJobsAlert } from "./DisapprovedJobsAlert";
import { VideoTipsAccess } from "./components/VideoTipsAccess";

import { Feature } from "@hire/schema";
import { pxToRem } from "@otta/design-tokens";
import { Button, Text, Tipbox } from "@otta/design";
import { useAuthorizations } from "@hire/providers/authorization";
import { RemovedJobsExplanation } from "@hire/components/RemovedJobsModal";
import { PausedProUpsellPage } from "@hire/components/ProUpsell";
import { Icon } from "@otta/icons";
import { Loading } from "@otta/shared-components";
import {
  useNewUsersUpsell,
  useUserCompanySubscription,
} from "@hire/util/hooks";
import { NewUserUpsell } from "@hire/components/NewUserUpsell";

const ActionButton = styled(Button)`
  display: flex;
  gap: 0.5rem;
`;

const JobsPageContainer = styled.div`
  flex: 1;
  display: flex;
  background-color: white;
  min-height: 100%;
`;

const NotificationSidebar = styled.div`
  display: none;
  flex-direction: column;
  gap: lg;
  max-width: ${pxToRem(416)};
  padding: xl;
  background-color: beige-200;

  ${up(
    "desktop",
    css`
      display: flex;
    `
  )}

  &:empty {
    display: none;
  }
`;

const JobsListContainer = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

function CreateButton({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement {
  return (
    <ActionButton
      data-testid="create-job-button"
      onClick={() => onClick()}
      level="secondary"
    >
      <Icon icon="plus" />
      <Text bold>Create new job</Text>
    </ActionButton>
  );
}

function SwapButton(): React.ReactElement {
  return (
    <ActionButton level="secondary" to="choose-job-slots" as={Link}>
      <Icon icon="arrow-right-arrow-left" />
      <Text bold>Swap published jobs</Text>
    </ActionButton>
  );
}

const storageKey = "has-seen-account-creation-modal";

export const JobsList = () => {
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [hasMissingSalary, setHasMissingSalary] = useState(false);

  const { features, loading: featuresLoading } = useAuthorizations([
    Feature.CreateJob,
    Feature.ViewJobs,
    Feature.JobSlots,
  ]);

  const { showUpsell, isFirstLogin, loading } = useNewUsersUpsell();

  const {
    loading: subscriptionLoading,
    isPAYGCustomer,
    corePaid,
  } = useUserCompanySubscription();

  const navigate = useNavigate();

  const hasSeenAccountCreationModal = useRef(
    localStorage.getItem(storageKey)
  ).current;

  useEffect(() => {
    if (
      !loading &&
      !subscriptionLoading &&
      !hasSeenAccountCreationModal &&
      isFirstLogin &&
      isPAYGCustomer
    ) {
      localStorage.setItem(storageKey, "true");
      navigate("./account-creation");
    }
  }, [
    loading,
    subscriptionLoading,
    isFirstLogin,
    isPAYGCustomer,
    navigate,
    hasSeenAccountCreationModal,
  ]);

  const hasJobSlots =
    !!features.get(Feature.JobSlots)?.granted &&
    features.get(Feature.JobSlots)?.message !== "WARNING";

  const jobListMode: JobListMode = hasJobSlots
    ? JobListMode.ONLY_PAUSED_JOBS
    : JobListMode.ALL_JOBS;

  const viewJobsFeature = features.get(Feature.ViewJobs);
  const createJobFeature = features.get(Feature.CreateJob);

  const isPaused = createJobFeature?.message === "paused";
  const showCreate = isPaused || createJobFeature?.granted;

  const createButton = showCreate && (
    <CreateButton
      onClick={() => {
        if (createJobFeature?.message === "paused") {
          setShowPausedModal(true);
        } else {
          navigate("./create");
        }
      }}
    />
  );

  const actionButtons = (
    <ButtonContainer>
      {createButton}
      {hasJobSlots && <SwapButton />}
    </ButtonContainer>
  );

  return (
    <>
      <JobsPageContainer data-testid="jobs-list-container">
        <JobsListContainer>
          {hasJobSlots && viewJobsFeature?.granted && (
            <PublishedJobList
              createButton={showCreate && createButton}
              swapButton={hasJobSlots && <SwapButton />}
              onMissingSalary={() => setHasMissingSalary(true)}
            />
          )}
          {featuresLoading || loading || subscriptionLoading ? (
            <Loading />
          ) : viewJobsFeature?.message === "paused" ? (
            <PausedProUpsellPage />
          ) : viewJobsFeature?.granted ? (
            <FilterableJobList
              mode={jobListMode}
              actionButtons={!hasJobSlots && actionButtons}
              onMissingSalary={() => setHasMissingSalary(true)}
            />
          ) : (
            <Tipbox level="error">
              You don't have permission to view this page.
            </Tipbox>
          )}
        </JobsListContainer>
        <NotificationSidebar>
          {showUpsell && <NewUserUpsell />}
          <DisapprovedJobsAlert />
          {hasMissingSalary && <MissingSalaryWarning />}
          {isPAYGCustomer && (
            <VideoTipsAccess
              text="Click here to learn more about us, how to see results and navigate the platform."
              ctaText="Video tips"
              ctaAnalyticsId="payg-account-creation-cta"
              onCTAClick={() => navigate("./account-creation")}
              testId="payg-access"
            />
          )}
          {isPAYGCustomer && corePaid && (
            <VideoTipsAccess
              text="You're on Startup Essential."
              ctaText="Learn how to be an expert"
              ctaAnalyticsId="payg-subscription-creation-cta"
              onCTAClick={() => navigate("./subscription-creation")}
              testId="payg-subscription-creation"
            />
          )}
        </NotificationSidebar>
      </JobsPageContainer>
      <RemovedJobsExplanation enabled={showPausedModal} />
      <Outlet />
    </>
  );
};
